<script setup lang="ts">

import { ref, watch } from 'vue';
import { NumericValidator, makeInComponentValidationWrapper } from './inputValidation'

const props = defineProps<{
  validator: NumericValidator,
}>();

const emit = defineEmits(['valueChange'])
const unityFraction = defineModel<number>({default: 0});
const root = ref<HTMLInputElement|undefined>(undefined);

// Event Flow
// ----------
//
//          OUTSIDE
//           |  ^
//  v-model  |  | v-model
//           v  |
//          MODEL (unityFraction)
//           |  ^
//   (A)     |  |  (B)
// on change |  | watch, parse
// & format  |  | & reformat
//           |  | on change
//           v  |
//          ELEMENT (root)

const validate = makeInComponentValidationWrapper<number>(
  root, unityFraction, props.validator);

// (A) unityFraction -> root
watch(unityFraction, (newValue: number, _oldValue: number) => {
  setRootValue(newValue);
  validate(undefined);
});

// (B) root -> unityFraction
function onRootChanged(evt: Event) {
  const incoming = root.value?.value ?? '0%';
  const s = incoming.trim();
  const hasPercentSuffix = s.at(-1) == '%';
  const ss = hasPercentSuffix ? s.substring(0, s.length-1) : s;
  const n = parseFloat(ss) / (hasPercentSuffix ? 100 :  1);

  if (unityFraction.value != n) {
    unityFraction.value = n;
    emit('valueChange', evt);
  }

  setRootValue(n);
};

function setRootValue(unityFraction: number) {
  function setStringValue(value: string) {
    if (root.value) {
      root.value.value = value;
      root.value.innerText = value;
    }
  }

  if (unityFraction == 0) {
    setStringValue('0%');
    return;
  }

  if (Number.isNaN(unityFraction)) {
    setStringValue('0%');
    return;
  }

  if (!unityFraction) {
    setStringValue('?');
    return;
  }

  let s = (unityFraction * 100).toString();

  const dpPos = s.indexOf('.');
  if (dpPos != -1) {
    const nd = s.length - dpPos - 1;  // number of digits after decimal
    if (nd > 2) {

      // maybe pass in as config param later?
      const MAX_POST_POINT_DIGITS = 2;

      const trunc = nd - MAX_POST_POINT_DIGITS;
      const newLength = s.length - trunc;
      s = s.substring(0, newLength);
    }
  }

  setStringValue(s + '%');
}


</script>

<template>
  <input
    class="fin-input-percentage"
    ref="root"
    type="text"
    @change="onRootChanged"
  />
  </input>
</template>

<style scoped>

input {
  width: 110px;
}

</style>
